/**
 * useApprovalClaim — reconciles approval lease state for the room panel.
 *
 * Inputs:
 *   - networkId / poolId / currentUserSub  → identify the pool + caller
 *   - agentMessages                         → live AppSync events from
 *     ``useAgentChannel`` (we react to APPROVAL_CLAIMED / DELEGATED /
 *     ESCALATED / RESOLVED to mutate the local roster incrementally).
 *
 * Responsibilities:
 *   1. Hydrate the live lease roster via REST on mount / pool change.
 *   2. Merge incremental AppSync beacons on top so every pool member
 *      sees "Jane is reviewing…" within one roundtrip of her click.
 *   3. Surface imperative ``claim()`` / ``delegate()`` / ``escalate()``
 *      so the approval panel can stay presentational.
 *   4. Expose ``getApproval(taskId)`` — a single lookup the panel uses
 *      when the Approve/Deny/Modify click needs to decide whether to
 *      submit or redirect to a "claim first" CTA.
 *
 * Failure posture: all imperative actions return the updated lease on
 * success and re-throw the API error on 4xx (caller renders a toast).
 * Background hydration is best-effort; a transient 5xx during mount
 * just leaves ``leases: []`` until the first AppSync frame arrives.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  listApprovals,
  claimApproval as apiClaim,
  delegateApproval as apiDelegate,
  escalateApproval as apiEscalate,
} from '../services/approvalsApi';

const APPROVAL_EVENT_TYPES = new Set([
  'APPROVAL_REQUESTED',
  'APPROVAL_CLAIMED',
  'APPROVAL_DELEGATED',
  'APPROVAL_ESCALATED',
  // The sweeper publishes this when a CLAIMED lease's TTL lapses but
  // the overall SLA hasn't fired yet. The row flips back to OPEN so any
  // pool supervisor can re-claim; the event carries the prior claimer's
  // identity for the audit UI.
  'APPROVAL_CLAIM_EXPIRED',
  'APPROVAL_RESOLVED',
]);

function toInt(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function leaseFromEvent(evt) {
  // Events don't ship the full lease row — they ship the mutation. We
  // patch the known fields; anything missing stays whatever the
  // previous local lease held. This keeps the UI lively even when the
  // REST hydrator hasn't re-run yet.
  const payload = (evt && evt.payload) || {};
  const taskId = evt?.correlationId || payload.taskId;
  if (!taskId) return null;
  const patch = { taskId };
  const type = (evt && evt.eventType) || '';
  switch (type) {
    case 'APPROVAL_REQUESTED':
      patch.status = 'OPEN';
      if (payload.agentName) patch.agentName = payload.agentName;
      break;
    case 'APPROVAL_CLAIMED':
      patch.status = 'CLAIMED';
      if (payload.claimerSub) patch.claimerSub = payload.claimerSub;
      if (payload.claimerEmail) patch.claimerEmail = payload.claimerEmail;
      if (toInt(payload.expiresAt) !== null) patch.expiresAt = toInt(payload.expiresAt);
      patch.live = true;
      break;
    case 'APPROVAL_DELEGATED':
      patch.status = 'CLAIMED';
      if (payload.toSub) patch.claimerSub = payload.toSub;
      if (payload.toEmail) patch.claimerEmail = payload.toEmail;
      if (payload.fromSub) patch.delegatedFrom = payload.fromSub;
      if (payload.toSub) patch.delegatedTo = payload.toSub;
      if (payload.reason) patch.delegateReason = payload.reason;
      if (toInt(payload.expiresAt) !== null) patch.expiresAt = toInt(payload.expiresAt);
      patch.live = true;
      break;
    case 'APPROVAL_ESCALATED':
      patch.status = 'ESCALATED';
      patch.claimerSub = undefined;
      patch.claimerEmail = undefined;
      if (payload.escalatedBy) patch.escalatedBy = payload.escalatedBy;
      if (payload.escalationTarget) patch.escalationTarget = payload.escalationTarget;
      if (payload.reason) patch.escalationReason = payload.reason;
      patch.live = true;
      break;
    case 'APPROVAL_CLAIM_EXPIRED':
      // Automatic reset from the sweeper — the previous claimer walked
      // away. Flip back to OPEN and wipe claim-specific fields so the
      // Room's "Claim to review" button reappears for everyone.
      patch.status = 'OPEN';
      patch.claimerSub = undefined;
      patch.claimerEmail = undefined;
      patch.claimedAt = undefined;
      patch.delegatedTo = undefined;
      if (payload.previousClaimerSub) {
        patch.previousClaimerSub = payload.previousClaimerSub;
      }
      if (payload.previousClaimerEmail) {
        patch.previousClaimerEmail = payload.previousClaimerEmail;
      }
      patch.live = true;
      break;
    case 'APPROVAL_RESOLVED':
      patch.status = 'RESOLVED';
      if (payload.decision) patch.decision = payload.decision;
      if (payload.feedback) patch.feedback = payload.feedback;
      patch.live = false;
      break;
    default:
      return null;
  }
  return patch;
}

export function useApprovalClaim(networkId, poolId, agentMessages = [], currentUserSub = '') {
  const [leasesByTask, setLeasesByTask] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const seenEventCountRef = useRef(0);

  // REST hydration on every (networkId, poolId) pivot. Cancel-safe — the
  // ``alive`` guard prevents a stale response from flipping a fresher
  // roster back to the old pool after a rapid tab switch.
  useEffect(() => {
    if (!networkId || !poolId) {
      setLeasesByTask({});
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    listApprovals(networkId, poolId)
      .then((resp) => {
        if (!alive) return;
        const next = {};
        for (const lease of resp.approvals || []) {
          if (lease?.taskId) next[lease.taskId] = lease;
        }
        setLeasesByTask(next);
        seenEventCountRef.current = Array.isArray(agentMessages) ? agentMessages.length : 0;
      })
      .catch((err) => {
        if (!alive) return;
        setError(err);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydration only on pool pivots; message merge is separate effect
  }, [networkId, poolId]);

  // Merge incremental AppSync frames. We only inspect messages newer
  // than ``seenEventCountRef`` to keep this O(new-frames) rather than
  // O(lifetime-frames) even on rooms that have streamed 10k events.
  useEffect(() => {
    if (!Array.isArray(agentMessages) || agentMessages.length === 0) return;
    const start = seenEventCountRef.current;
    if (agentMessages.length <= start) return;
    let changed = false;
    const draft = { ...leasesByTask };
    for (let i = start; i < agentMessages.length; i += 1) {
      const frame = agentMessages[i];
      if (!frame || typeof frame !== 'object') continue;
      const type = frame.eventType;
      if (!APPROVAL_EVENT_TYPES.has(type)) continue;
      const patch = leaseFromEvent(frame);
      if (!patch || !patch.taskId) continue;
      const prior = draft[patch.taskId] || {};
      draft[patch.taskId] = { ...prior, ...patch };
      changed = true;
    }
    seenEventCountRef.current = agentMessages.length;
    if (changed) setLeasesByTask(draft);
  }, [agentMessages, leasesByTask]);

  const refresh = useCallback(async () => {
    if (!networkId || !poolId) return;
    const resp = await listApprovals(networkId, poolId);
    const next = {};
    for (const lease of resp.approvals || []) {
      if (lease?.taskId) next[lease.taskId] = lease;
    }
    setLeasesByTask(next);
  }, [networkId, poolId]);

  const claim = useCallback(
    async (taskId, ttlSeconds = 300) => {
      if (!networkId || !poolId || !taskId) throw new Error('claim: missing args');
      try {
        const { lease } = await apiClaim(networkId, poolId, taskId, ttlSeconds);
        setLeasesByTask((prev) => ({ ...prev, [taskId]: { ...(prev[taskId] || {}), ...lease } }));
        return lease;
      } catch (err) {
        // 409 ships the current lease so we can still refresh the UI —
        // the caller renders a toast but the panel snaps to reality.
        if (err && err.status === 409 && err.data && err.data.lease) {
          setLeasesByTask((prev) => ({
            ...prev,
            [taskId]: { ...(prev[taskId] || {}), ...err.data.lease },
          }));
        }
        throw err;
      }
    },
    [networkId, poolId],
  );

  const delegate = useCallback(
    async (taskId, args) => {
      if (!networkId || !poolId || !taskId) throw new Error('delegate: missing args');
      const { lease } = await apiDelegate(networkId, poolId, taskId, args);
      setLeasesByTask((prev) => ({ ...prev, [taskId]: { ...(prev[taskId] || {}), ...lease } }));
      return lease;
    },
    [networkId, poolId],
  );

  const escalate = useCallback(
    async (taskId, args = {}) => {
      if (!networkId || !poolId || !taskId) throw new Error('escalate: missing args');
      const { lease } = await apiEscalate(networkId, poolId, taskId, args);
      setLeasesByTask((prev) => ({ ...prev, [taskId]: { ...(prev[taskId] || {}), ...lease } }));
      return lease;
    },
    [networkId, poolId],
  );

  const getApproval = useCallback(
    (taskId) => (taskId ? leasesByTask[taskId] : undefined),
    [leasesByTask],
  );

  const isSelfClaimer = useCallback(
    (taskId) => {
      const l = taskId ? leasesByTask[taskId] : null;
      return !!(l && l.claimerSub && currentUserSub && l.claimerSub === currentUserSub && l.live);
    },
    [leasesByTask, currentUserSub],
  );

  return useMemo(
    () => ({
      leasesByTask,
      loading,
      error,
      claim,
      delegate,
      escalate,
      refresh,
      getApproval,
      isSelfClaimer,
    }),
    [leasesByTask, loading, error, claim, delegate, escalate, refresh, getApproval, isSelfClaimer],
  );
}
